import { compose } from 'ramda';

interface StepState {
  list: number[],
  index: number,
  keyIndex: number,
  flag: boolean,
  secondFlag: boolean,
  interval: number,
  temp: number,
  gap: number
};

function bubbleSortStep(
    state: StepState
) : StepState {
    const {
        list,
        index,
        keyIndex: lastIndex,
        flag: swapped
    } = state;

    if (lastIndex === 0) {
        return state;
    }
    if (index === lastIndex-1) {
        if (swapped) {
            return compose(restartIndex,
                           decreaseLastIndex,
                           swapFalse) (state);
        } else {
            return compose(restartIndex, zeroLastIndex) (state);
        }
    } else if (list[index] > list[index+1]) {
        return compose(moveForward, swapForward, flagTrue) (state);
    } else {
        return moveForward(state);
    }
}

function insertionSortStep(
    state: StepState
) : StepState {
    const {
        list,
        index,
        keyIndex,
        flag: inserted
    } = state;

    if (keyIndex === list.length) {
        return state;
    }
    if (index === 0 || inserted) {
        return nextKey(state);
    } else if (list[index-1] > list[index]) {
        return compose(moveBackward, swapBackward) (state);
    } else {
        return flagTrue(state);
    }
}

function selectionSortStep (
    state: StepState
) : StepState {
    const { list, index, keyIndex: lastIndex } = state;

    if (lastIndex === list.length) {
        return state;
    }

    if (index === list.length) {
        return restartIndexToKeyIndex(state);
    } else if ( list[index] < list[lastIndex] ) {
        return moveForward(
            swapWithKeyIndex(state)
        );
    } else {
        return moveForward(state);
    }
}

function combSortStep(
    state: StepState
) : StepState {
    const { list, index, keyIndex: lastIndex, gap, flag } = state;

    if(gap === 0 && flag === false){
        return lastSwap(state);
    }
    else if(gap === 0 && flag === true && index === list.length){
        return state;
    }

    if (index === list.length) {
        return compose(recountGap, restartIndexToBegining) (state);
    } else if (list[index] < list[lastIndex]) {
        return compose(restartIndexToKeyIndexWithGap, swapWithKeyIndex) (state);
    } else {
        return restartIndexToKeyIndexWithGap(state);
    }
}

function shellSortStep (
    state : StepState
    ) : StepState {
        const {
            flag: secondLoop, 
            secondFlag: thirdLoop,
            interval 
        } = state;
        if (interval > 0) {
            if (thirdLoop) {
                return checkSwapInterval(state);
            } else if (secondLoop) {
                return checkInterval(state);
            } else {
                return checkInterval({ ...state, index: interval, flag: true});
            }
        }
        else {
            return state;
        }
}

function countingSortStep(state: StepState) : StepState {
    const { list, index, keyIndex: lastSwapIndex, temp: counter } = state;
    if (lastSwapIndex === list.length) {
        return state;
    }
    if (index === list.length) {
        return { ...state, temp: state.temp + 1, index: lastSwapIndex};
    } else if (list[index] === counter) {
        return moveForward(swapToLastIndex(state));
    } else {
        return moveForward(state);
    }
}

function cocktailSortStep(
    state: StepState
) : StepState {
    const {
        list,
        index,
        keyIndex: lastIndex,
        temp: firstIndex,
        flag: swapped,
        secondFlag: forward
    } = state;

    if (firstIndex === lastIndex) {
        return state;
    }
    if (index === lastIndex && forward) {
        if (swapped) {
            return compose(moveBackward, moveBackward, decreaseLastIndex, flipDirection, swapFalse) (state);
        } else {
            return state;
        }
    } else if (index === firstIndex - 1 && !forward) {
        if (swapped) {
            return compose(moveForward, increaseFirstIndex, flipDirection, swapFalse) (state);
        } else {
            return state;
        }
    } else if (list[index] > list[index + 1]) {
        if (forward) {
            return compose(moveForward, swapForward, flagTrue) (state);
        } else {
            return compose(moveBackward, swapForward, flagTrue) (state);
        }
    } else {
        if (forward) {
            return moveForward(state);
        } else {
            return moveBackward(state);
        }
    }
}

function gnomeSortStep(
    state: StepState
) : StepState {
    const {
        list,
        index,
        keyIndex: listLength
    } = state;

    if (index === listLength) {
        return state;
    }
    if (index === 0) {
        return moveForward(state);
    }
    if (list[index] >= list[index-1]){
        return moveForward(state);
    } else {
        return compose(moveBackward, swapBackward) (state);  
    }
}

function brickSortStep(
    state: StepState
) : StepState {
    const {
        list,
        index,
        keyIndex: lastIndex,
        flag: swapped,
        secondFlag: odd
    } = state;

    if (index > lastIndex - 1) {
        if (odd) {
            return compose(flipDirection, restartIndex) (state);
        } else {
            if (!swapped) {
                return state;
            } else {
                return compose(flipDirection, moveForward, restartIndex, swapFalse) (state);
            }   
        }
    } else {
        if (list[index] > list[index+1]) {
            return compose(moveForward, moveForward, swapForward, flagTrue) (state);
        } else {
            return compose(moveForward, moveForward) (state);
        }
    }
}

function recountGap(state: StepState) : StepState {
    const {gap} = state;
    const newGap = Math.floor( gap*10/13 );
    return { ...state, gap: newGap}
}

function lastSwap(state: StepState) : StepState {
    return { ...state, gap: 1, flag: true}
}

function checkInterval(state: StepState) : StepState {
    const { 
        list, 
        index: outer,
        interval
    } = state;
    if (outer < list.length) {
        return checkSwapInterval({ ...state, keyIndex: outer, secondFlag: true, temp: list[outer]});
    } else {
        return { ...state, list: list, interval: Math.floor(interval/2), flag: false}
    }
}

function checkSwapInterval(state: StepState) : StepState {
    const { 
        list, 
        index: outer, 
        keyIndex: inner,
        interval,
        temp 
    } = state;
    if (inner >= interval && list[inner - interval] > temp) {
        list[inner] = list[inner - interval]
        return { ...state, list: list, keyIndex: inner - interval}
    } else {
        list[inner] = temp;
        return { ...state, list: list, index: outer + 1, secondFlag: false}
    }
}

function swapWithKeyIndex(state: StepState) : StepState {
    const {list, index, keyIndex: lastIndex} = state;
    const newList = list.slice(0, lastIndex)
                        .concat(list[index])
                        .concat(list.slice(lastIndex + 1, index))
                        .concat(list[lastIndex])
                        .concat(list.slice(index + 1, list.length))
    return { ...state, list: newList}
}

function swapToLastIndex(state: StepState) : StepState {
    const { list, index, keyIndex: lastSwapIndex } = state;
    const newList = list.slice(0, lastSwapIndex)
                        .concat(list[index])
                        .concat(list.slice(lastSwapIndex, index))
                        .concat(list.slice(index + 1, list.length));
    return { ...state,
        list: newList,
        keyIndex: state.keyIndex + 1,
    }
}

function swapForward(state: StepState) : StepState {
    const { list, index } = state;
    const newList = list.slice(0, index)
                        .concat(list[index+1])
                        .concat(list[index])
                        .concat(list.slice(index+2));
    return { ...state, list: newList }
}

function swapBackward(state: StepState) : StepState {
    const { list, index } = state;
    const newList = list.slice(0, index-1)
                        .concat(list[index])
                        .concat(list[index-1])
                        .concat(list.slice(index+1));
    return { ...state, list: newList }
}

function moveForward(state: StepState) : StepState {
    return { ...state, index: state.index+1 }
}

function restartIndex(state: StepState) : StepState {
    return { ...state, index: 0 }
}

function restartIndexToKeyIndexWithGap(state: StepState) : StepState {
    return { ...state, 
        keyIndex: state.keyIndex + 1, 
        index: state.index + 1,
    }
}

function restartIndexToKeyIndex(state: StepState) : StepState {
    return { ...state, 
        keyIndex: state.keyIndex + 1, 
        index: state.keyIndex + 1,
    }
}

function restartIndexToBegining(state: StepState) : StepState {
    const {gap} = state;
    return { ...state, 
        keyIndex: 0, 
        index: gap,
    }
}

function decreaseLastIndex(state: StepState) : StepState {
    return { ...state, keyIndex: state.keyIndex-1 }
}

function swapFalse(state: StepState) : StepState {
    return { ...state, flag: false }
}

function flagTrue(state: StepState) : StepState {
    return { ...state, flag: true }
}

function zeroLastIndex(state: StepState) : StepState {
    return { ...state, keyIndex: 0 }
}

function moveBackward(state: StepState) : StepState {
    return { ...state, index: state.index-1}
}

function nextKey(state: StepState) : StepState {
    return { ...state, keyIndex: state.keyIndex+1, index: state.keyIndex+1, flag: false }
}

function increaseFirstIndex(state: StepState) : StepState {
    return { ...state, temp: state.temp + 1}
}

function flipDirection(state: StepState) : StepState {
    return { ...state, secondFlag: !state.secondFlag}
}

export {
    bubbleSortStep,
    insertionSortStep,
    selectionSortStep,
    combSortStep,
    shellSortStep,
    countingSortStep,
    cocktailSortStep,
    gnomeSortStep,
    brickSortStep
}

export type {
    StepState
}
